/**
 * 🦵 **Cutting the settler's legs off so they can swing** — T173.
 *
 * 🎯 **haugur, seeing the character in play, 2026-09-07:** *"the carachter dont
 * move limbs, they just glide around like goshts hehe."*
 *
 * ## 🔴 A photographed person is a STATUE
 *
 * A photogrammetry scan is **one rigid mesh with no joints**. It cannot walk,
 * swing an arm or turn its head, and no amount of texture work changes that
 * (`Development/Minecraft/16` §11.2).
 *
 * ✅ **The way out is the research's own finding: Minecraft's player has no
 * skeleton either — it is six rigid boxes rotated about pivots, animated by
 * arithmetic.** So this needs **no `SkinnedMesh`, no `AnimationMixer` and no
 * rigging pipeline**, none of which this client has.
 *
 * ## 🔴 Why only the LEGS, and why the cut is where it is
 *
 * **His cloak hangs to the knee and his arms are underneath it.** Cutting arms
 * out would tear the cloak and show the seam from two metres. **Below the hem
 * there are trousers and boots, fully visible — and legs are what read as
 * walking.**
 *
 * ⚠️ **The hem was MEASURED, not guessed.** Across the model's height the
 * silhouette is 0.53 wide at 35% and 0.76 at 40%: **that widening is the cloak.**
 * So the cut is at **{@link LEG_CUT} of the model's height**, just under it.
 *
 * 🔴 **The legs therefore swing from the HEM, not from a hip** — and that is
 * right rather than a compromise: **the cloak hides where the real joint is**,
 * which is exactly how a person in a long coat looks when they walk.
 *
 * ## ⚠️ Pure arithmetic, no `three`
 *
 * **So it tests without a browser and the design tool can draw the same split.**
 * *A sheet that redraws the thing is a second copy of the decision* — the fault
 * on record against `pictureItemFor`.
 */

/**
 * 📏 **Where the cut goes, as a fraction of the model's height.**
 *
 * 🔴 **Measured off `player.glb`:** the silhouette is 0.53 units wide at 35% of
 * height and 0.76 at 40% — **the cloak flaring out.** 0.36 is under that, in the
 * trousers.
 *
 * ⚠️ **A new character model wants this looked at again**, exactly as
 * `burgeom.ts` says of its own tuned numbers. **The thing that makes it wrong is
 * a garment of a different length**, so that is what to grep for.
 */
export const LEG_CUT = 0.36;

/** One part of the split: triangles, and the texture coordinates that go with them. */
export interface Part {
  readonly p: number[];
  readonly u: number[];
}

/** The settler in three pieces. */
export interface Split {
  /** Everything above the hem — cloak, torso, head, arms. Never moves. */
  readonly body: Part;
  readonly left: Part;
  readonly right: Part;
}

/**
 * 🦵 **Split a scan into a body and two legs, by where each triangle sits.**
 *
 * @param positions Vertex positions, `x, y, z` repeating, **three vertices per
 *   triangle and NOT indexed** — call `toNonIndexed()` first. ⚠️ **In the
 *   model's own frame with the FEET AT y = 0**, which is the frame everything
 *   else about this character is in.
 * @param uvs Texture coordinates, `u, v` per vertex, or `null` for a mesh
 *   without any. ⚠️ **They must be split alongside the positions** or the legs
 *   come out wearing a piece of the sky.
 * @param cutY The hem, in the same units as `positions`.
 * @param centreX Where the middle of the body is — **left and right are decided
 *   against this, not against zero**, because a scan is not perfectly centred.
 *
 * ⚠️ **Whole triangles, decided by their centroid.** Splitting a triangle
 * properly would need new vertices along the cut; **at this size the difference
 * is one triangle's worth of ragged edge, hidden under a hem.**
 */
export function splitLegs(
  positions: ArrayLike<number>,
  uvs: ArrayLike<number> | null,
  cutY: number,
  centreX: number,
): Split {
  const body: Part = { p: [], u: [] };
  const left: Part = { p: [], u: [] };
  const right: Part = { p: [], u: [] };

  for (let i = 0; i + 8 < positions.length; i += 9) {
    /* eslint-disable security/detect-object-injection -- reads at an arithmetic
       offset into a vertex array, as `sampleTriangle` in `burgeom.ts` does. */
    const cy = ((positions[i + 1] ?? 0) + (positions[i + 4] ?? 0) + (positions[i + 7] ?? 0)) / 3;
    const cx = ((positions[i] ?? 0) + (positions[i + 3] ?? 0) + (positions[i + 6] ?? 0)) / 3;
    const part = cy >= cutY ? body : cx < centreX ? left : right;
    for (let k = 0; k < 9; k++) part.p.push(positions[i + k] ?? 0);
    if (uvs) {
      const j = (i / 9) * 6;
      for (let k = 0; k < 6; k++) part.u.push(uvs[j + k] ?? 0);
    }
    /* eslint-enable security/detect-object-injection */
  }
  return { body, left, right };
}

/** 🐑 Which way a four-legged body's long axis runs, in the model's own frame. */
export type LongAxis = 'x' | 'z';

/**
 * 🐑 **Where one leg stands**, in the horizontal plane of the model's own frame.
 *
 * ⚠️ **`long` runs nose-to-tail and `across` runs flank-to-flank**, whichever of
 * x and z those turn out to be — an image-to-3D model does not promise which.
 * 🔴 **Measured off the model's own hoof-level footprint**, never assumed:
 * `tools/design-sheep-body.mjs --measure` clusters the vertices near the ground
 * and hands back the centre of each lump it finds.
 */
export interface LegAnchor {
  readonly long: number;
  readonly across: number;
}

/** A four-legged animal in six pieces. */
export interface QuadSplit {
  /** The barrel and the fleece. **Never moves.** */
  readonly body: Part;
  /**
   * 🐑 **The head, which TURNS AND DROPS** — and it is a part of its own because
   * haugur asked for it in as many words.
   *
   * 🎯 **haugur, 2026-09-23:** *"sheep needs to be able to walk aroun, run a
   * little, raise, lower and turn it s hea, we are going for lifelike
   * movement."*
   *
   * 🔴 **Mojang's bone tree says the same thing structurally**
   * ([`Minecraft/19`](../../../Development/Minecraft/19_Animal_Animation.md)
   * §1.6): **the head is a child of `root` and a SIBLING of `body`.** *Rotating
   * the head does not drag the body; rotating the body does drag the legs.*
   * **The bone tree is the coupling specification**, and here it is a list of
   * five parts.
   *
   * ⚠️ **Empty when no neck was given** — a creature with no separate head is
   * not an error, it is a cow with a bad photograph.
   */
  readonly head: Part;
  /** 🔴 **In the order the anchors were given**, so the caller owns which is which. */
  readonly legs: readonly Part[];
}

/**
 * 🐑 **Where the neck is, and which way the head lies from it.**
 *
 * `from` is a position along the body: **everything beyond it, and above the leg
 * cut, is head.** ⚠️ **Measured off the model's own narrowing**, never guessed —
 * a fleeced sheep's neck is much thinner than its barrel, which is the same
 * silhouette-widening trick `LEG_CUT` uses for a cloak.
 */
export interface Neck {
  readonly from: number;
}

/**
 * 🕳️ **CLOSE THE HOLE A CUT LEAVES BEHIND.**
 *
 * 🎯 **haugur, 2026-09-23, and it is the correct root cause where three of mine
 * were not:** *"you take a pic, slice it to create joints, joints make movement
 * of the model, mesh does not compinsate the added surface area of the model, so
 * all added surface area is a hole."*
 *
 * 🔴 **A generated model is a HOLLOW SHELL.** Slicing it makes **two** new faces
 * — one on the part, one on the body — **and nothing ever generates either.**
 * At rest the two pieces sit inside one another and hide it. **The moment a
 * joint moves you look through the open face, into the hollow inside, and
 * straight out the far side**, because the interior is back-facing and culled.
 *
 * ⚠️ **THE COLLAR WAS NEVER A FIX FOR THIS — it is a blindfold.** It duplicates
 * a band of skin to stand in front of the hole. 📏 **Measured: growing it from
 * 0.06 to 0.22 closed the neck and made every leg drag 19 cm of flank.** 👉
 * ***Making a blindfold bigger is not closing a hole.***
 *
 * ## 🔬 How
 *
 * **A boundary edge is one that belongs to exactly ONE triangle of this part.**
 * Every cut leaves a ring of them. ✅ **Group them into loops and fan each loop
 * to its own centroid**, which closes the shell.
 *
 * ⚠️ **Vertices are matched on a QUANTISED key.** Two triangles that share an
 * edge hold their own copies of those coordinates, and floating point makes them
 * differ in the last bits — **an exact match finds no shared edges at all and
 * every edge looks like a boundary.**
 *
 * ⚠️ **A model that was ALREADY open gets capped too**, which is correct: an
 * open shell is a hole whoever made it.
 *
 * ⚠️ **UVs on a cap are the loop's own average.** The cut face is inside the
 * animal and is only ever glimpsed; a plausible flat colour beats a seam of
 * garbage texture.
 */
function capCuts(part: Part): number {
  /**
   * 🔴 **FEWER THAN FOUR TRIANGLES CANNOT ENCLOSE A VOLUME** — a tetrahedron is
   * the smallest closed shell there is. **So there is no interior to see into
   * and nothing to cap**, and a "hole" found here is the patch's own rim.
   *
   * ⚠️ **Without this, a single triangle grew a COPLANAR fan of three more** —
   * 9 numbers became 36 — **and fifteen tests in this file said so.** 👉 *A
   * failing old test is evidence before it is a chore*: they were right, the
   * cap was junk, and the guard is the geometry rather than a number that makes
   * them pass.
   */
  if (part.p.length < 4 * 9) return 0;

  const KEY = 1e4;
  const key = (x: number, y: number, z: number): string =>
    `${Math.round(x * KEY)},${Math.round(y * KEY)},${Math.round(z * KEY)}`;

  /* ── every directed edge, so a boundary edge is one with no partner ── */
  const seen = new Map<string, number>();
  const at = (i: number): [number, number, number] => [
    // eslint-disable-next-line security/detect-object-injection -- a loop counter into a plain array, as the rest of this file does.
    part.p[i] ?? 0,
    part.p[i + 1] ?? 0,
    part.p[i + 2] ?? 0,
  ];
  const edges: [string, string][] = [];
  for (let i = 0; i + 8 < part.p.length; i += 9) {
    const k = [key(...at(i)), key(...at(i + 3)), key(...at(i + 6))];
    for (let e = 0; e < 3; e++) {
      // eslint-disable-next-line security/detect-object-injection -- a loop counter into a plain array, as the rest of this file does.
      const a = k[e] ?? '';
      const b = k[(e + 1) % 3] ?? '';
      const pair = a < b ? `${a}|${b}` : `${b}|${a}`;
      seen.set(pair, (seen.get(pair) ?? 0) + 1);
      edges.push([a, b]);
    }
  }

  const pos = new Map<string, [number, number, number]>();
  for (let i = 0; i + 2 < part.p.length; i += 3) {
    const v = at(i);
    pos.set(key(...v), v);
  }

  /* ── the boundary, as a walkable map of next-vertex ── */
  const next = new Map<string, string[]>();
  for (const [a, b] of edges) {
    const pair = a < b ? `${a}|${b}` : `${b}|${a}`;
    if ((seen.get(pair) ?? 0) !== 1) continue;
    const list = next.get(a);
    if (list) list.push(b);
    else next.set(a, [b]);
  }

  let capped = 0;
  const done = new Set<string>();
  const uvAvg: [number, number] = [0, 0];
  if (part.u.length > 0) {
    for (let i = 0; i + 1 < part.u.length; i += 2) {
      // eslint-disable-next-line security/detect-object-injection -- a loop counter into a plain array, as the rest of this file does.
      uvAvg[0] += part.u[i] ?? 0;
      uvAvg[1] += part.u[i + 1] ?? 0;
    }
    uvAvg[0] /= part.u.length / 2;
    uvAvg[1] /= part.u.length / 2;
  }

  for (const start of next.keys()) {
    if (done.has(start)) continue;
    /* ── walk the loop ── */
    const loop: string[] = [];
    let cur: string | undefined = start;
    while (cur !== undefined && !done.has(cur)) {
      done.add(cur);
      loop.push(cur);
      cur = next.get(cur)?.find((n) => !done.has(n));
    }
    /* ⚠️ Three is the smallest thing that can be a hole. */
    if (loop.length < 3) continue;

    let cxx = 0;
    let cyy = 0;
    let czz = 0;
    for (const k of loop) {
      const v = pos.get(k) ?? [0, 0, 0];
      cxx += v[0];
      cyy += v[1];
      czz += v[2];
    }
    cxx /= loop.length;
    cyy /= loop.length;
    czz /= loop.length;

    for (let i = 0; i < loop.length; i++) {
      // eslint-disable-next-line security/detect-object-injection -- a loop counter into a plain array, as the rest of this file does.
      const a = pos.get(loop[i] ?? '') ?? [0, 0, 0];
      const b = pos.get(loop[(i + 1) % loop.length] ?? '') ?? [0, 0, 0];
      part.p.push(cxx, cyy, czz, a[0], a[1], a[2], b[0], b[1], b[2]);
      if (part.u.length > 0)
        part.u.push(uvAvg[0], uvAvg[1], uvAvg[0], uvAvg[1], uvAvg[0], uvAvg[1]);
      capped++;
    }
  }
  return capped;
}

/**
 * 🐑 **Split a photographed quadruped into a body and four legs.**
 *
 * 🔴 **THE TWO-LEG VERSION OF THIS SHIPPED IN SEPTEMBER AND WORKS**
 * ({@link splitLegs}); this is the same idea with two differences that both
 * come out of research written before either existed.
 *
 * ## ① The cut is a RAMP, not a height
 *
 * ⚠️ [`Minecraft/18`](../../../Development/Minecraft/18_Animal_Movement_And_AI.md)
 * §8.3 flagged it: *"the cut plane differs fore and hind."* **A sheep's brisket
 * hangs lower than its belly**, so one flat height either saws through the chest
 * or leaves the forelegs as stumps.
 *
 * ✅ **So `cutAt` is a FUNCTION of where you are along the body**, and a ramp
 * rather than a step — 🔴 **a step would leave a visible stair in the seam at
 * the midpoint**, which is the sort of thing that looks like a modelling fault
 * and is actually a cutting fault.
 *
 * ## ② A leg is claimed by the NEAREST anchor, not by a midpoint
 *
 * **Two perpendicular midpoint tests would be the obvious way** — fore/hind and
 * left/right — and it is the wrong one: **a real animal does not stand square**,
 * and a leg placed slightly across an invented centre line goes to the wrong
 * part and swings backwards.
 *
 * ✅ **Nearest anchor cannot do that**, because the anchors are where the legs
 * actually are. ⚠️ **It also cannot fail silently:** if the model fused two legs
 * the measurement finds three lumps rather than four, **and the caller knows
 * before this function is ever called.**
 *
 * @param positions Vertex positions, `x, y, z` repeating, **three vertices per
 *   triangle and NOT indexed** — call `toNonIndexed()` first. ⚠️ **In the
 *   model's own frame with the HOOVES AT y = 0.**
 * @param uvs Texture coordinates, `u, v` per vertex, or `null`. ⚠️ **Split
 *   alongside the positions** or a leg comes out wearing a piece of the fleece.
 * @param cutAt Given a position along the body, the height of the cut there.
 * @param anchors Where each leg stands. **Normally four.** ⚠️ **An empty list
 *   puts everything in the body** rather than throwing, so a fused model still
 *   draws and the measurement is what reports the fault.
 * @param longAxis Which of `x`/`z` runs nose-to-tail.
 * @param legReach How far from its anchor a leg may claim geometry.
 * @param collar 🔴 **How far PAST its cut a moving part also keeps a copy of
 *   the body's surface, in metres — and this is what stops a joint opening.**
 *
 *   🎯 **haugur, 2026-09-23:** *"when the sheep is walking and turning its head
 *   and stuf, there come a air gap between the cuts so sometime its looks like
 *   the neck or legs are detached for a split second."*
 *
 *   🔴 **A PARTITION CANNOT AVOID THIS AND NO AMOUNT OF BETTER ART WILL.** Every
 *   triangle belonged to exactly one part, so a leg's flat cut face and the
 *   body's flat cut face are the *same* surface — **rotate one and you are
 *   looking straight through the joint.**
 *
 *   ✅ **Mojang never has it because its parts are BOXES that overlap inside the
 *   body** (`Minecraft/19` §1.5a: every part is a rigid box, and they
 *   intersect). 👉 **So the parts must OVERLAP rather than partition**: a
 *   collar of the body's own surface travels with the moving part and sweeps
 *   over the seam, while the body keeps its copy and covers the socket.
 *
 *   ⚠️ **The two copies coincide exactly at rest**, so nothing is visible until
 *   something turns — which is precisely when it is needed. ⚠️ **It costs
 *   triangles**, so it is a band and not a half.
 *
 * ⚠️ **Whole triangles, decided by their centroid** — {@link splitLegs}'s own
 * compromise, for the same reason: splitting a triangle properly needs new
 * vertices along the cut, and **at this size the difference is one triangle of
 * ragged edge, hidden under the fleece.**
 */
export function splitQuadruped(
  positions: ArrayLike<number>,
  uvs: ArrayLike<number> | null,
  cutAt: (long: number) => number,
  anchors: readonly LegAnchor[],
  longAxis: LongAxis,
  neck: Neck | null = null,
  legReach: number = Infinity,
  collar = 0,
  neckCollar = collar,
): QuadSplit {
  const body: Part = { p: [], u: [] };
  const head: Part = { p: [], u: [] };
  const legs: Part[] = anchors.map(() => ({ p: [], u: [] }));

  for (let i = 0; i + 8 < positions.length; i += 9) {
    /* eslint-disable security/detect-object-injection -- reads at an arithmetic
       offset into a vertex array, as `splitLegs` above does. */
    const cx = ((positions[i] ?? 0) + (positions[i + 3] ?? 0) + (positions[i + 6] ?? 0)) / 3;
    const cy = ((positions[i + 1] ?? 0) + (positions[i + 4] ?? 0) + (positions[i + 7] ?? 0)) / 3;
    const cz = ((positions[i + 2] ?? 0) + (positions[i + 5] ?? 0) + (positions[i + 8] ?? 0)) / 3;
    const long = longAxis === 'x' ? cx : cz;
    const across = longAxis === 'x' ? cz : cx;

    let part = body;
    /* 🐑 The head first: beyond the neck and clear of the legs. ⚠️ ABOVE the leg
       cut on purpose — a sheep grazing puts its nose near the ground, and a head
       that stole the forelegs would swing them with it. */
    if (neck !== null && long >= neck.from && cy >= cutAt(long)) part = head;
    else if (cy < cutAt(long) && anchors.length > 0) {
      let best = 0;
      let bestDist = Infinity;
      for (let a = 0; a < anchors.length; a++) {
        const anchor = anchors[a];
        if (!anchor) continue;
        const dl = long - anchor.long;
        const da = across - anchor.across;
        const dist = dl * dl + da * da;
        if (dist < bestDist) {
          bestDist = dist;
          best = a;
        }
      }
      /**
       * 🔴 **A LEG IS A COLUMN, NOT A HALF-PLANE — and leaving this out tore
       * the belly off.**
       *
       * 🎯 **haugur, issue #745:** *"their bellys seem to move a little up and
       * down and glitch away from the body."*
       *
       * ⚠️ **Nearest-anchor alone claims EVERYTHING below the cut**, and most of
       * what is down there between four legs is **brisket and belly.** So the
       * belly was handed to whichever leg happened to be closest and **swung
       * with it**, tearing away from the barrel at every stride.
       *
       * ✅ **A triangle further from its own anchor than {@link legReach} stays
       * with the BODY**, which is where a belly belongs.
       */
      part = bestDist <= legReach * legReach ? (legs[best] ?? body) : body;
    }

    const push = (into: Part): void => {
      for (let k = 0; k < 9; k++) into.p.push(positions[i + k] ?? 0);
      if (uvs) {
        const j = (i / 9) * 6;
        for (let k = 0; k < 6; k++) into.u.push(uvs[j + k] ?? 0);
      }
    };
    push(part);

    /**
     * 🔴 **THE COLLAR — a second copy that travels with the moving part.**
     *
     * ⚠️ **Only for triangles the BODY kept**, and only just past a cut. A
     * triangle already in a leg must not be copied anywhere, or the leg would
     * carry a duplicate of itself.
     */
    if ((collar > 0 || neckCollar > 0) && part === body) {
      const cut = cutAt(long);
      /* 🦵 Just above a leg's cut, and close enough to that leg to be its
         shoulder rather than the belly. */
      if (collar > 0 && cy >= cut && cy < cut + collar && anchors.length > 0) {
        let best = -1;
        let bestDist = Infinity;
        for (let a = 0; a < anchors.length; a++) {
          const anchor = anchors[a];
          if (!anchor) continue;
          const dl = long - anchor.long;
          const da = across - anchor.across;
          const dist = dl * dl + da * da;
          if (dist < bestDist) {
            bestDist = dist;
            best = a;
          }
        }
        const leg = best >= 0 && bestDist <= legReach * legReach ? legs[best] : undefined;
        if (leg !== undefined) push(leg);
      }
      /* 🐑 And just behind the neck, for the head. */
      if (neck !== null && long < neck.from && long >= neck.from - neckCollar && cy >= cut)
        push(head);
    }
    /* eslint-enable security/detect-object-injection */
  }
  /* 🕳️ Close every hole the cut just made — haugur's diagnosis. See capCuts. */
  for (const part of [body, head, ...legs]) capCuts(part);

  return { body, head, legs };
}

/**
 * 🐑 **The four-legged walk: ONE cosine, diagonal pairs, and that is all of it.**
 *
 * 🔴 **Read off Mojang's own published files, in BOTH editions** —
 * [`Minecraft/19`](../../../Development/Minecraft/19_Animal_Animation.md) §1.1.
 * `quadruped.animation.json` rotates four legs by `cos(t × 38.17°)` at **±80°**,
 * signs **`+ − − +`**, and **nothing else on the animal moves**: no body bob, no
 * spine flex, no foot planting, no knee. **That is the walk cycle of the cow,
 * the pig, the sheep and the horse.**
 *
 * ## 🔴 The two traps, and both are in the research rather than in the code
 *
 * **① THE ×4.** Minecraft's `0.6662` reads an accumulator that advances at
 * **four times the metres walked**, so copying the constant across gives a gait
 * **four times too slow** — and it will read as a fault in the model. ✅ **Like
 * {@link legSwing}, this takes REAL METRES and carries its own rate**, so the
 * trap cannot arrive here.
 *
 * **② ±80° IS A LICENCE A CUBOID HAS AND A PHOTOGRAPH DOES NOT.** §8.3 and
 * `14_The_Animal_System.md` §③ both say so: **a photographed limb with no knee
 * looks broken long before a box does.** The settler swings **0.35 rad**, and
 * 🐑 **a sheep's legs are shorter and stiffer, so this is less again.**
 *
 * @param distance How far this animal has walked in total, in metres.
 * @param stride How far it travels in one step, in metres. 🔴 **Derive it from
 *   the animal's own leg length rather than passing a number somebody liked** —
 *   see {@link QUAD_SWING_RADIANS}.
 * @returns Four angles in radians, **hind-left, hind-right, fore-left,
 *   fore-right**, in the diagonal pattern `+ − − +`. ⚠️ **Getting the sign
 *   pattern wrong is the whole difference between a walking animal and a
 *   pantomime horse, and it costs two minus signs.**
 */
export function quadSwing(distance: number, stride: number, amplitude = 1): readonly number[] {
  const phase = (Math.PI / stride) * distance;
  const a = Math.sin(phase) * QUAD_SWING_RADIANS * amplitude;
  return [a, -a, -a, a];
}

/**
 * 🏃 **How far the legs should swing right now — a SMOOTHED speed, 0 to 1.**
 *
 * 🎯 **haugur, 2026-09-23:** *"sheep needs to be able to walk aroun, run a
 * little."* 🔴 **This is what makes walking and running one animation instead of
 * two**, and it is Mojang's, in both editions
 * ([`Minecraft/19`](../../../Development/Minecraft/19_Animal_Animation.md)
 * §1.2a):
 *
 * ```java
 * float f1 = Math.min(f * 4.0F, 1.0F);   // f = distance moved this tick
 * speed   += (f1 - speed) * 0.4F;        // exponential lag, 0.4 per tick
 * ```
 *
 * 🔴 **THE 0.4 LAG IS THE PART THAT MATTERS AND THE PART A CLONE LEAVES OUT.**
 * Without it **the legs snap to a full swing the instant an animal steps off**
 * and snap flat the instant it stops. ⚠️ *"Amplitude scaled by a smoothed speed"*
 * is listed as **load-bearing** in 19's own verdict table, with the reason
 * written next to it: *without it a standing animal treads air, and a starting
 * one snaps.*
 *
 * ⚠️ **`legsplit.ts`'s own settler does NOT have this** and gets away with it
 * because distance simply stops advancing. **A sheep that can run needs it.**
 *
 * 📏 **Saturates at 5 m/s**, which is Minecraft's `0.25 blocks/tick × 4 = 1.0`
 * expressed in our units — above that the gait is purely time-based.
 *
 * @param previous Last tick's value. Start at `0`.
 * @param metresPerSecond How fast it is actually travelling.
 * @param dt Seconds since the last tick.
 */
export function limbSpeed(
  previous: number,
  metresPerSecond: number,
  dt: number,
  topSpeed: number = LIMB_SATURATION,
): number {
  /**
   * 🔴 **SCALE AGAINST THE ANIMAL'S OWN TOP SPEED, NOT MOJANG'S 5 m/s — AND
   * LEAVING THIS OUT MADE THE SHEEP GLIDE.**
   *
   * 🎯 **haugur, issue #745:** *"the fly around or glide on the earth, no leg
   * movement."*
   *
   * 📏 **Mojang saturates at 5 m/s because that is roughly a SPRINTING PLAYER.**
   * ⚠️ **A sheep walks at 0.9** — so the swing came out at **18% of an already
   * modest 17°, which is about THREE DEGREES.** Invisible, and the animal reads
   * as sliding on ice.
   *
   * 👉 ***It is the same trap the graze drop avoided and this did not.*** That
   * one is kept as a **share of the animal's height**, because Mojang's sheep is
   * 1.3 m and ours is 0.85; **this was left as an absolute from a different
   * body.** 🔴 **A borrowed number needs the thing it was measured against.**
   */
  const want = Math.min(Math.max(metresPerSecond, 0) / Math.max(topSpeed, 0.01), 1);
  /* 🔴 0.4 per TICK at Minecraft's 20 ticks a second, expressed for any dt —
     otherwise the smoothing would depend on our beat rather than on time. */
  const rate = 1 - Math.pow(1 - 0.4, dt * 20);
  return previous + (want - previous) * rate;
}

/** 📏 The speed at which the gait stops getting faster — Mojang's own ceiling. */
export const LIMB_SATURATION = 5;

/** 🐑 Where a head is pointing, relative to the body it sits on. */
export interface HeadPose {
  /** Left and right, radians. `0` is straight ahead. */
  readonly yaw: number;
  /** 🔴 **Up and down, radians. POSITIVE IS DOWN**, which is Mojang's sign. */
  readonly pitch: number;
}

/**
 * 🐑 **Turn a head toward something, at a believable rate.**
 *
 * 🎯 **haugur:** *"raise, lower and turn it s hea."*
 *
 * 🔴 **No IK, no look-at rig, no blend tree** —
 * [`Minecraft/19`](../../../Development/Minecraft/19_Animal_Animation.md) §1.6:
 * in Java the head is **a direct assignment with no smoothing at all**, and all
 * the work happens before the model sees it, in **three exponential lags and two
 * clamps.** This is the two that matter for a head:
 *
 * | | |
 * |---|---|
 * | 🔴 **75°** | how far the head may LEAD the body before the body has to turn |
 * | 🔴 **10° per tick** | how fast it gets there — 200° a second |
 *
 * ⚠️ **In Minecraft the 75° clamp only runs while the mob is PATHING**, so a
 * standing animal can crane further round. **That is visible in play and easy to
 * miss** — 19 §1.6 flags it — and it is deliberately NOT copied here, because a
 * head that unwinds the moment an animal starts walking reads as a glitch.
 *
 * @param from Where the head is now.
 * @param wantYaw Desired yaw relative to the body, radians.
 * @param wantPitch Desired pitch, radians, **positive down**.
 * @param dt Seconds since the last tick.
 */
export function headTowards(
  from: HeadPose,
  wantYaw: number,
  wantPitch: number,
  dt: number,
): HeadPose {
  const most = HEAD_TURN_PER_SECOND * dt;
  const step = (now: number, want: number): number => {
    const delta = want - now;
    if (delta > most) return now + most;
    if (delta < -most) return now - most;
    return want;
  };
  const yaw = step(from.yaw, Math.min(Math.max(wantYaw, -HEAD_LEAD), HEAD_LEAD));
  const pitch = step(from.pitch, Math.min(Math.max(wantPitch, -HEAD_LEAD), HEAD_LEAD));
  return { yaw, pitch };
}

/**
 * 📐 **75°** — how far a head may lead its body. Mojang’s number.
 *
 * 🔴 **RE-EXPORTED FROM `shared`, never redefined here.** The brain has to
 * choose an idle glance inside this clamp, and it could not see the number
 * while it lived in the client. ⚠️ *A second copy is how a thin þorskur that
 * was never thin got invented.*
 */
import { HEAD_LEAD } from '@lofi/shared';
export { HEAD_LEAD };

/**
 * 🧥 **How much body-skin a moving part carries past its own cut, as a share of
 * the animal's height.**
 *
 * 🎯 **haugur:** *"there come a air gap between the cuts so sometime its looks
 * like the neck or legs are detached for a split second."*
 *
 * 🔴 **IT LIVES HERE BECAUSE IT WAS WRITTEN DOWN TWICE.** `sheepmesh.ts` had a
 * `COLLAR_SHARE` and `tools/design-sheep-body.mjs` had a bare `m.height * 0.06`
 * — **so the sheet drew one sheep and the game drew another**, and tuning the
 * dial would have moved the picture without moving the game. 👉 ***A second way
 * in that forgets what the first one does***, recorded five times in this repo
 * before this one.
 *
 * ⚠️ **A DIAL.** Too thin and a joint opens at its extreme; too thick and a part
 * drags a skirt of body with it. 🔬 **Judge it on the bottom row of
 * `102_sheep_cut.png`** — the head at its full {@link HEAD_LEAD} clamp and the
 * legs at the stride extreme, which is the only picture that can show a joint
 * failing at all.
 */
export const COLLAR_SHARE = 0.06;

/**
 * 🧥 **THE NECK'S OWN COLLAR, AND IT IS FOUR TIMES THE LEGS'.**
 *
 * 🔴 **ONE NUMBER CANNOT SERVE BOTH JOINTS, AND TRYING COST AN EVENING.**
 * 📏 A leg swings **{@link SWING_RADIANS} ≈ 20°**; a head turns
 * **{@link HEAD_LEAD} = 75°.** 📏 **Measured on the sheet: at 0.06 the turned
 * head opened a gap you could see the world through; at 0.22 it closed — and
 * every leg then dragged 19 cm of flank with it.**
 *
 * 🎯 **haugur named the other half of this**, which capping fixed: *"mesh does
 * not compinsate the added surface area of the model, so all added surface area
 * is a hole."* 👉 ***There are TWO holes at a joint*** — **through** an open cut
 * face, which {@link capCuts} closes, and **between** two closed parts that have
 * moved apart, which only overlap can close. **Both are needed.**
 *
 * ⚠️ **A DIAL, judged on the bottom row of `102_sheep_cut.png`** — the only
 * picture that poses the joints at their limits.
 */
export const NECK_COLLAR_SHARE = 0.22;
/** 📐 **10° a tick = 200° a second.** Mojang's number, in our units. */
export const HEAD_TURN_PER_SECOND = (10 * Math.PI) / 180 / 0.05;

/**
 * 🌿 **A sheep with its head down, eating — and the chew is what sells it.**
 *
 * 🔴 **THE CLEANEST EDITION AGREEMENT IN THE WHOLE RESEARCH LIBRARY.**
 * [`Minecraft/19`](../../../Development/Minecraft/19_Animal_Animation.md) §1.6:
 * Bedrock's `sheep.animation.json` and Java's `Sheep`/`SheepModel` describe the
 * **same animation to seven significant figures** — 2.0 s, a **9-unit** head
 * drop, **36°** of pitch, and a chew jitter of **`0.07π` radians** at about
 * **2.85 wobbles a second**, held for the middle 1.6 s with a 4-tick ramp each
 * end.
 *
 * 👉 ***One extra sine on one bone, and a sheep is GRAZING rather than
 * bowing.***
 *
 * 🔴 **WHAT THE HEAD DOES IS NO LONGER HERE — it is `GRAZE_BODY` in
 * `sheeprig.ts` (#820).** Mojang's 36° and 9-unit drop are for a box head on one
 * pivot, and on our rig they left the nose at chest height. This returns only
 * HOW FAR DOWN (0 … 1) and the chew; the body decides what "down" looks like.
 *
 * ## 🔴 HOW LONG IS THE SERVER'S, NOT THIS FILE'S
 *
 * 🎯 **haugur, 2026-10-01:** *"they drop their head for just a second and then
 * keep walking"* → **8 seconds**. ⚠️ **This file used to carry its own 2-second
 * length and raised the head AT it** — harmless while the mind's number was also
 * 2, and the moment it became 8 the head would have come up after two seconds
 * of a sheep standing frozen for six more, then dipped AGAIN as the count ran
 * back down. ✅ **So the head now follows the server's `Graze` itself**
 * ({@link grazeStep}): down while it lasts, up when it ends.
 *
 * @returns `down`, 0 … 1, and `chew` in radians for the skull.
 */
export function grazePose(state: GrazeState): { down: number; chew: number } {
  const down = Math.min(Math.max(state.down, 0), 1);
  /* 🔴 The chew: about 2.85 a second, and ONLY while down. */
  const chew = Math.sin(state.seconds * 2.85 * 2 * Math.PI) * GRAZE_CHEW * down;
  return { down, chew };
}

/** 🌿 **Where a grazing head is**: how long it has eaten (the chew's phase) and how far down, 0 … 1. */
export interface GrazeState {
  readonly seconds: number;
  readonly down: number;
}

/** ⏱️ **0.2 s down and 0.2 s up** — Mojang's 4-tick ramp, so the head never snaps. */
export const GRAZE_RAMP = 0.2;

/**
 * 🌿 **One frame of a grazing head** — down while the server says `Graze`, up
 * when it stops, and `null` once it is all the way up.
 */
export function grazeStep(
  state: GrazeState | null,
  grazing: boolean,
  dt: number,
): GrazeState | null {
  if (grazing) {
    const was = state ?? { seconds: 0, down: 0 };
    return { seconds: was.seconds + dt, down: Math.min(1, was.down + dt / GRAZE_RAMP) };
  }
  if (state === null) return null;
  const down = state.down - dt / GRAZE_RAMP;
  return down <= 0 ? null : { seconds: state.seconds + dt, down };
}
/**
 * 🫨 **The chew, radians either way — on the SKULL only.**
 *
 * 🎯 **haugur, #820:** *"bows its head to chest heihgt and bobs around."* 🔴
 * **Mojang's 0.07π (12.6°) was put on the whole neck**, so the whole head
 * bobbed. Their head is one box on one pivot; ours is a skull on a neck, and a
 * chew is the skull. 📏 **At 0.03π the muzzle nibbles about 2 cm back and
 * forth at the grass** (measured on the rig sheet) and never leaves it.
 */
export const GRAZE_CHEW = 0.03 * Math.PI;

/**
 * 📐 **How far a quadruped's leg swings at the extreme, in radians** — about 17°.
 *
 * ⚠️ **NOT a measured number and NOT haugur's** — it is the settler's 0.35 rad
 * (20°) reduced because **a sheep's leg is shorter, stiffer and fully visible**,
 * where a person's swings from under a cloak. 🔴 **Mojang's ±80° is four and a
 * half times this and is correct for a BOX.**
 *
 * 👉 **This is a playtest dial and must not be defended as a design.** ***A
 * measurement can prove a leg swings; it cannot tell you the sheep looks
 * alive*** — `14_The_Animal_System.md`, and haugur's eye is the instrument.
 */
export const QUAD_SWING_RADIANS = 0.3;

/**
 * 🚶 **How far through a stride somebody is, from how far they have walked.**
 *
 * 🔴 **Driven by DISTANCE, not by time** — *"a sine of the distance travelled"*
 * (`Development/Minecraft/16` §2). **That is what makes a slow walk swing slowly
 * and a run swing fast without anybody tuning a second number**, and it is why
 * the legs stop dead when somebody stands still rather than treading air.
 *
 * @param distance How far this person has walked in total, in metres.
 * @returns The angle to swing the LEFT leg, in radians. **The right leg is the
 *   negative of it.**
 */
export function legSwing(distance: number): number {
  return Math.sin(distance * STRIDE_RATE) * SWING_RADIANS;
}

/**
 * How many radians of phase a metre of walking is worth.
 *
 * ⚠️ **A full stride is two steps**, so this is `2π ÷ stride length`. At 0.75 m
 * a step that is one step every 0.75 m, which is an ordinary walking pace for a
 * person 1.8 m tall.
 */
const STRIDE_RATE = Math.PI / 0.75;

/**
 * 📐 **How far a leg swings at the extreme, in radians** — about 20°.
 *
 * ⚠️ **Deliberately modest.** The leg is being swung from the HEM rather than
 * from a hip, so a real hip's 35–40° would look like the shin bending backwards
 * at the knee. **What is wanted is enough motion to stop the gliding**, not a
 * march.
 */
const SWING_RADIANS = 0.35;
